import { inject, Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { ParsedOptionSymbolInterface } from '@modules/options-module/general/interfaces/parsed-option-symbol.interface'
import { RegExpEnum } from '@shared/enums/reg-exp.enum'
import { OptionShortTypeEnum } from '@modules/options-module/general/enums/option-short-type.enum'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { DateTime } from 'luxon'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { SocketStockTInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-t.interface'
import { SocketStockQInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-q.interface'
import { SocketPricesHelperService } from '@modules/prices-module/socket-prices/services/socket-prices-helper.service'
import { SocketStockEvEnum } from '@modules/prices-module/socket-prices/enums/socket-stock-ev.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'

@Injectable({
	providedIn: 'root',
})
export class OptionsHelperService {
	protected readonly number: NumberConstructor = Number
	protected readonly regExp: RegExpConstructor = RegExp
	protected readonly date: DateConstructor = Date
	protected readonly dateTime: typeof DateTime = DateTime
	protected readonly regExpEnum: typeof RegExpEnum = RegExpEnum
	protected readonly optionShortTypeEnum: typeof OptionShortTypeEnum = OptionShortTypeEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly socketStockEvEnum: typeof SocketStockEvEnum = SocketStockEvEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly infinity: number = Infinity

	public activeOptionContract$: BehaviorSubject<OptionContractInterface | null> =
		new BehaviorSubject<OptionContractInterface | null>(null)

	private readonly _socketPricesHelperService: SocketPricesHelperService =
		inject<SocketPricesHelperService>(SocketPricesHelperService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)

	public parseOptionSymbol(symbol: string): ParsedOptionSymbolInterface | null {
		const regexp: RegExp = new this.regExp(this.regExpEnum.OPTION_SYMBOL)
		const symbolRegexp: RegExpExecArray | null = regexp.exec(symbol)
		if (!symbolRegexp) return null

		const [, ticker, yyyy, mm, dd, type, strikePrice]: RegExpExecArray = symbolRegexp
		if (!ticker || !yyyy || !mm || !dd || !type || !strikePrice) return null

		const year: number = 2000 + this.number(yyyy)
		const date: string = `${year}-${mm}-${dd}`

		return {
			option_ticker: ticker,
			option_strike: +strikePrice / 1000,
			option_type:
				(type as OptionShortTypeEnum) === this.optionShortTypeEnum.CALL
					? this.optionTypeEnum.CALL
					: this.optionTypeEnum.PUT,
			option_date: date,
		}
	}

	public checkReachedDate1515ET(date: string | undefined): boolean {
		if (!date) return false

		const [yearStr, monthStr, dayStr]: (string | undefined)[] = date.split('-')
		if (!yearStr || !monthStr || !dayStr) return false

		const day: number = parseInt(dayStr, 10)
		const month: number = parseInt(monthStr, 10)
		const year: number = parseInt(yearStr, 10)

		if ([day, month, year].some((n: number): boolean => this.number.isNaN(n))) {
			return false
		}

		const calendarDate: Date = new this.date(year, month - 1, day)
		const today: Date = new this.date()
		const todayStart: Date = new this.date(today.getFullYear(), today.getMonth(), today.getDate())
		const thresholdET: DateTime = this.dateTime.fromObject(
			{ year, month, day, hour: 15, minute: 15 },
			{ zone: 'America/New_York' },
		)
		const nowET: DateTime = this.dateTime.now().setZone('America/New_York')
		return todayStart >= calendarDate && nowET.toMillis() >= thresholdET.toMillis()
	}

	public fillOption(option: OptionContractInterface, socketPrices: SocketStocksType[]): OptionContractInterface {
		const marketStatus: MarketStatusEnum | null = this._marketStatusHelperService.marketStatus

		const ticker: string = option.details.ticker
		const strike: number = option.details.strike_price
		const lastDayPrice: number = option.day.close
		const prevClosePrice: number = option.day.previous_close

		const socketPricesByOption: SocketStocksType[] = socketPrices.filter(
			(stock: SocketStocksType): boolean => stock.sym === option.details.ticker,
		)

		const stockT: SocketStockTInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.T,
			socketPricesByOption,
			ticker,
		) as SocketStockTInterface | undefined

		const stockQ: SocketStockQInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.Q,
			socketPricesByOption,
			ticker,
		) as SocketStockQInterface | undefined

		if (!stockT && !stockQ) return option

		const optionPrice: number = stockT?.p ?? option.last_trade.price
		const bidSize: number = stockQ?.bs ?? option.last_quote.bid_size
		const askSize: number = stockQ?.as ?? option.last_quote.ask_size
		const bid: number = stockQ?.bp ?? option.last_quote.bid
		const ask: number = stockQ?.ap ?? option.last_quote.ask
		const midpointPrice: number = (bid + ask) / 2
		const changeMoney: number = midpointPrice - prevClosePrice
		const changePercent: number = (changeMoney / prevClosePrice) * 100

		let breakEven: number = option.break_even_price

		switch (option.details.contract_type) {
			case this.optionTypeEnum.CALL:
				if (marketStatus === this.marketStatusEnum.marketOpen) {
					breakEven = optionPrice ? strike + optionPrice : (breakEven ?? strike)
				} else {
					breakEven = strike + (lastDayPrice ?? 0)
				}
				break

			case this.optionTypeEnum.PUT:
				if (marketStatus === this.marketStatusEnum.marketOpen) {
					breakEven = optionPrice ? strike - optionPrice : (breakEven ?? strike)
				} else {
					breakEven = strike - (lastDayPrice ?? 0)
				}
				break
		}

		return {
			...option,
			break_even_price: breakEven,
			day: {
				...option.day,
				close: midpointPrice,
				change: !changeMoney || changeMoney === this.infinity ? 0 : changeMoney,
				change_percent: !changePercent || changePercent === this.infinity ? 0 : changePercent,
			},
			last_quote: {
				...option.last_quote,
				bid,
				ask,
				bid_size: bidSize,
				ask_size: askSize,
				midpoint: midpointPrice,
			},
			last_trade: {
				...option.last_trade,
				price: midpointPrice,
			},
		}
	}
}
